import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface Props {
  seriesIndex?: any;
  dataPointIndex?: any;
  w: any;
  currentYear: string;
  selectedMonth?: string | null;
}

const CustomTooltip: React.FC<Props> = ({ seriesIndex, dataPointIndex, w, currentYear, selectedMonth }) => {
  const { t } = useTranslation();
  const seriesData = w.config.series[seriesIndex]?.data[dataPointIndex] || {};
  const monthAbbr = w.config.series[seriesIndex].name;

  const yValue = monthAbbr;
  const xValue = seriesData.x;
  const date = dayjs(`${currentYear}-${selectedMonth}-${yValue}`).format('ddd');
  const month = dayjs(`${currentYear}-${selectedMonth}-${yValue}`).format('MMM');

  const captureCount = seriesData.captureCount ?? 0;
  const nonCaptureCount = seriesData.nonCaptureCount ?? 0;
  const isUpcoming = seriesData.y === -1000000;
  const hasNoActivity = captureCount === 0 && nonCaptureCount === 0;

  const captureSignals = seriesData.times.filter((signal: string) => signal.includes('APPROVED_MICE'));

  const nonCaptureSignals = seriesData.times.filter((signal: string) => !signal.includes('APPROVED_MICE'));

  return (
    <div className="popover" style={{ width: '260px', maxWidth: '100%' }}>
      <div className="popover-header m-0 py-1 bg-light">
        <div className="d-flex align-items-center mx-2 justify-content-between gap-3">
          <div className="d-flex">
            {selectedMonth ? (
              <>
                {`${date} ${yValue}th `}
                <br />
                {`at ${xValue.split(':')[0]}:00 - ${xValue.split(':')[0]}:59`}
              </>
            ) : (
              <>{`${yValue} ${xValue}`}</>
            )}
          </div>

          {!isUpcoming && (
            <div className="d-flex justify-content-between flex-column" style={{ alignItems: 'flex-start' }}>
              <h6 className="m-0">
                {t('MCTDetail.heatmap.captures')}: {captureCount}
              </h6>
              <h6 className="m-0">
                {t('MCTDetail.heatmap.no_captures')}: {nonCaptureCount}
              </h6>
            </div>
          )}
        </div>
      </div>
      <div className="popover-body text-center">
        {hasNoActivity ? (
          <h6>
            {t('MCTDetail.heatmap.no_activity')} {t('MCTDetail.heatmap.on')}{' '}
            {seriesData.x.includes(':') ? `${month} ${monthAbbr}` : seriesData.x}
            {t('MCTDetail.heatmap.th')}{' '}
            {seriesData.x.includes(':') ? t('MCTDetail.heatmap.at') : t('MCTDetail.heatmap.of')}{' '}
            {!seriesData.x.includes(':') ? monthAbbr : `${seriesData.x}-${seriesData.x.replace(':00', ':59')}`}
          </h6>
        ) : (
          <div
            className="d-flex flex-column px-3"
            style={{ alignItems: 'flex-start', textAlign: 'left', width: '100%' }}
          >
            {captureSignals.length > 0 && (
              <div className="d-flex flex-column gap-1 w-100">
                {captureSignals.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const enter = parts[parts.length - 2];
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`capture-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}</div>

                      {enter === 'R' && (
                        <div className="d-flex align-items-center mr-2">
                          <i className="ri-arrow-right-line" />
                          <div>R</div>
                        </div>
                      )}

                      {enter === 'L' && (
                        <div className="d-flex align-items-center mr-2">
                          <div>L</div>
                          <i className="ri-arrow-left-line" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {nonCaptureSignals.length > 0 && (
              <div className="d-flex flex-column gap-1 w-100 mt-2">
                {nonCaptureSignals.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const enter = parts[parts.length - 2];
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`non-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}</div>

                      {enter === 'R' && (
                        <div className="d-flex align-items-center mr-2">
                          <i className="ri-arrow-right-line" />
                          <div>R</div>
                        </div>
                      )}

                      {enter === 'L' && (
                        <div className="d-flex align-items-center mr-2">
                          <div>L</div>
                          <i className="ri-arrow-left-line" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomTooltip;
